import { ActionIcon, Box, Group, Stack, Title } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { isEmpty } from 'lodash-es';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { groupBy, isDefined } from 'remeda';
import { getEnvIcon } from 'shared/utils/environment';

interface PageTitleProps {
  children: React.ReactNode;
  overrideTitle?: string;
}

export const PageTitle = ({ children, overrideTitle }: PageTitleProps) => {
  const history = useHistory();
  const company = useCompanyConfiguration();
  const companyName = company.name ?? '';

  const childrenArray = React.Children.toArray(children);
  const { actions, title, subtitle, back } = groupBy(childrenArray, classify);

  useEffect(() => {
    // Remove any icons or other elements from the title we display in the tab
    const titleString = overrideTitle ?? (title ?? []).filter(e => typeof e === 'string').join(' ');
    document.title = `${getEnvIcon()}${companyName} - ${titleString}`;

    return function cleanup() {
      document.title = `${getEnvIcon()}${companyName ?? 'Recovr'}`;
    };
  }, [title, companyName]);

  if (isDefined(actions) && actions.length > 1) {
    console.error('PageTitle can only have one TitleActions child');
    return null;
  }

  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap={0} mb="space5">
      <Stack gap={2}>
        <Group>
          {isDefined(back) && (
            <ActionIcon onClick={() => history.goBack()} size="lg" variant="light">
              <IconArrowLeft size={20} />
            </ActionIcon>
          )}
          <Title
            order={4}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: 'unset',
            }}
          >
            {title}
          </Title>
        </Group>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Stack>
      {!isEmpty(actions) ? (
        <Group justify="space-between" wrap="nowrap">
          {actions}
        </Group>
      ) : null}
    </Group>
  );
};

function classify(input: unknown) {
  if (React.isValidElement(input) && typeof input !== 'string' && typeof input.type !== 'string') {
    // @ts-expect-error
    if (input.type.displayName === 'TitleActions') return 'actions';
    // @ts-expect-error
    if (input.type.displayName === 'Subtitle') return 'subtitle';
    // @ts-expect-error
    if (input.type.displayName === 'Back') return 'back';
  }
  return 'title';
}

// #region Actions
interface TitleActionsProps {
  children: React.ReactNode;
}

const TitleActions = ({ children }: TitleActionsProps) => {
  if (children == null) return null;
  return (
    <Group justify="space-between" wrap="nowrap">
      {children}
    </Group>
  );
};

TitleActions.displayName = 'TitleActions';
// #endregion

// #region Subtitle
interface SubtitleProps {
  children: React.ReactNode;
}

const Subtitle = ({ children }: SubtitleProps) => {
  if (children == null) return null;
  return (
    <Box c="dimmed" size="sm" mt={4}>
      {children}
    </Box>
  );
};

Subtitle.displayName = 'Subtitle';
// #endregion

// #region Back

const Back = () => null;

Back.displayName = 'Back';
// #endregion

PageTitle.Actions = TitleActions;
PageTitle.Subtitle = Subtitle;
PageTitle.Back = Back;
